"use client";

/**
 * The error page for everything under the root layout. What it does with
 * each kind of error is decided in lib/error-recovery.ts: a stale build
 * or an error thrown in the browser reloads the page once by itself; a
 * server error shows the "500" and "Try again" asks the server again; a
 * page whose API did not answer says it is temporarily unavailable and
 * asks again by itself (the gateway sends that one as a 503). The copy
 * follows the URL's language: this page sits outside the locale provider.
 *
 * Its script loads on every page, so it stays small: Sentry is loaded
 * only when there is something to report.
 */
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useTransition } from "react";
import { usePathname, useRouter } from "next/navigation";
import { afterShown, autoRetryDelay, decide, errorCopy, errorKind, errorLocale, tryAgain } from "@/lib/error-recovery";
import { applyStoredTheme } from "@/lib/theme-context";

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [pending, startTransition] = useTransition();
  const kind = errorKind(error);
  const plan = useMemo(() => decide(error), [error]);
  const autoDelay = useMemo(
    () => (kind === "unavailable" ? autoRetryDelay(error, pathname || "/") : null),
    [error, kind, pathname],
  );
  const copy = errorCopy(kind, errorLocale(pathname), autoDelay !== null);

  const again = useCallback(() => {
    tryAgain(kind, {
      reload: () => window.location.reload(),
      refresh: () => router.refresh(),
      reset,
      transition: startTransition,
    });
  }, [kind, router, reset]);

  useEffect(() => {
    // The session replay keeps console errors with their stack.
    console.error("Unhandled error:", error);
    return afterShown(error, pathname, () => window.location.reload());
  }, [error, pathname]);

  // The unavailable page asks again by itself, after the network is back
  // when the browser knows it is offline.
  useEffect(() => {
    if (autoDelay === null) return;
    let cancelled = false;
    const fire = () => {
      if (cancelled) return;
      if (navigator.onLine === false) {
        window.addEventListener("online", fire, { once: true });
        return;
      }
      again();
    };
    const timer = window.setTimeout(fire, autoDelay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("online", fire);
    };
  }, [autoDelay, again]);

  // A failed full load arrives as Next's bare error document, whose theme
  // script never runs (lib/theme-context applyStoredTheme).
  useEffect(() => {
    applyStoredTheme();
  }, []);

  // A client page cannot export metadata: the tab title is set here.
  useEffect(() => {
    document.title = `${copy.title} | Mənzil.ai`;
  }, [copy.title]);

  if (plan === "reload") return <div className="min-h-screen bg-gray-50" aria-busy="true" />;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        {copy.code && <div className="text-6xl font-extrabold text-red-200 mb-4 tabular-nums">{copy.code}</div>}
        <h1 className="text-xl font-bold text-gray-900 mb-2">{copy.title}</h1>
        <p className="text-sm text-gray-500 mb-6">{copy.body}</p>
        <button
          type="button"
          onClick={again}
          disabled={pending}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-80"
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {copy.action}
        </button>
      </div>
    </div>
  );
}
